import { useCallback } from 'react'
import { Label } from 'reactstrap'
import { AsyncPaginate, type LoadOptions } from 'react-select-async-paginate'
import type { AddressBookEntry } from '@branditdev/mf-types'
import type { GroupBase } from 'react-select'
import branditRails from '../../../../api/brandit_rails'

type AddressOption = { value: string; label: string; customerAddressId?: number }

type Pagination = { page: number }

type PaginatedResponse<T> = {
  data: T[]
  meta: { current_page: number; total_pages: number }
}

// ArtworkField is serialized via raw to_json — no typelizer type exists yet
type ArtworkField = {
  id: number
  name: string
  html_label: string
  is_required: boolean
  placeholder?: string
}

const formatAddressLabel = (address: AddressBookEntry) =>
  [
    address.full_name,
    address.address_line_1,
    address.address_line_2,
    [address.city, address.state].filter(Boolean).join(', '),
    address.zip_code
  ]
    .filter(Boolean)
    .join(', ')

const toOption = (address: AddressBookEntry): AddressOption => {
  const label = formatAddressLabel(address)
  return { value: label, label, customerAddressId: address.id }
}

const ArtworkAddressBookSelect = ({
  artworkField,
  value,
  handleChange
}: {
  artworkField: ArtworkField
  value: AddressOption | null
  handleChange: (value: AddressOption | null, field: ArtworkField) => void
}) => {
  const { name, html_label, is_required } = artworkField

  const loadOptions: LoadOptions<AddressOption, GroupBase<AddressOption>, Pagination> = useCallback(
    async (search, _prev, additional) => {
      const page = additional?.page ?? 1

      const { data } = await branditRails.get<PaginatedResponse<AddressBookEntry>>(
        '/artworks/address_book',
        { params: { q: search || undefined, page, per_page: 20 } }
      )

      return {
        options: data.data.map(toOption),
        hasMore: data.meta.current_page < data.meta.total_pages,
        additional: { page: page + 1 }
      }
    },
    []
  )

  const selected = value?.value === '-1' ? null : value

  return (
    <>
      <Label className="format-label" htmlFor={name}>
        {`${is_required ? '* ' : ''}${html_label}`}
      </Label>
      <AsyncPaginate<AddressOption, GroupBase<AddressOption>, Pagination>
        inputId={name}
        value={selected}
        loadOptions={loadOptions}
        onChange={selected => handleChange(selected, artworkField)}
        additional={{ page: 1 }}
        debounceTimeout={300}
        placeholder={artworkField.placeholder || 'Search addresses...'}
        isClearable
        loadOptionsOnMenuOpen
      />
    </>
  )
}

export default ArtworkAddressBookSelect
